import { Globe2 } from 'lucide-react'
import { CheckoutBannerField } from './CheckoutBannerField'
import type { Ref } from 'react'
import { Banner, SegmentedControl, TextField, Toggle } from '@shared/design-system/ui'
import { checkoutAccentHex, checkoutAccentLabels } from '@entities/product/api/product.contract'
import type {
  CheckoutAccent,
  CheckoutTemplate,
  ProductCheckout,
} from '@entities/product/api/product.contract'
import type { CheckoutExperienceMode } from '@entities/product/api/product.queries'
import { isHttpsRedirectUrl } from '@shared/lib/url/absolute-http-url'
import type { CheckoutSkin } from '../lib/checkoutStyle'
import {
  controlStack,
  copy,
  fieldBlock,
  fieldLabel,
  globalMarketCard,
  globalMarketIcon,
  globalMarketPill,
  group,
  groupHead,
  groupHint,
  groupTitle,
  inlineRow,
  nodeTitle,
  swatch,
  swatchCaption,
  toggleCard,
  toggleGrid,
} from './CheckoutBuilder.css'

export type CheckoutConfigControlsProps = {
  checkout: ProductCheckout
  onChange: (input: Partial<ProductCheckout>) => void
  thankYouUrlRef?: Ref<HTMLInputElement>
  originalThankYouUrl?: string
  experienceMode?: CheckoutExperienceMode
  skin?: CheckoutSkin
}

const blockLabels = {
  timer: 'Faixa "Oferta por tempo limitado"',
  reviews: 'Faixa "Compra protegida"',
  bump: 'Order bump',
  security: 'Selos de segurança',
}

const legacyOnlyBlocks = new Set<keyof typeof blockLabels>(['timer', 'reviews'])

const blockHints: Partial<Record<keyof typeof blockLabels, string>> = {
  timer: 'Aparece no topo da versão anterior do checkout. É um aviso fixo, sem cronômetro.',
  reviews: 'Aparece no topo da versão anterior do checkout.',
}

const fieldLabels = {
  name: 'Nome completo',
  email: 'E-mail',
  phone: 'Telefone',
  document: 'CPF',
}

const requiredFields = new Set<keyof ProductCheckout['fields']>(['email'])
const accentOptions: CheckoutAccent[] = ['green', 'blue', 'violet', 'orange']
export const thankYouUrlError = 'Informe uma URL completa, começando com https://.'

export function CheckoutSharedFields({
  checkout,
  onChange,
  thankYouUrlRef,
  originalThankYouUrl,
}: CheckoutConfigControlsProps) {
  const hasValidThankYouUrl = isHttpsRedirectUrl(checkout.thankYouUrl, originalThankYouUrl, {
    allowEmpty: true,
    allowHash: true,
  })

  return (
    <section className={group}>
      <div className={groupHead}>
        <span className={groupTitle}>Dados do checkout</span>
        <p className={groupHint}>
          O nome e a página de obrigado valem para o Checkout 1 e o Checkout 2.
        </p>
      </div>

      <TextField
        label="Nome do checkout"
        value={checkout.name}
        onChange={(event) => onChange({ name: event.target.value })}
      />

      <TextField
        ref={thankYouUrlRef}
        label="URL da página de obrigado"
        helperText="Opcional. Se preenchido, após a compra o comprador é redirecionado para esta página (com ?saleId=): use com o snippet do upsell na sua página."
        error={hasValidThankYouUrl ? undefined : thankYouUrlError}
        value={checkout.thankYouUrl}
        onChange={(event) => onChange({ thankYouUrl: event.target.value })}
      />
    </section>
  )
}

export function CheckoutConfigControls({
  checkout,
  onChange,
  experienceMode,
  skin = 'legacy',
}: CheckoutConfigControlsProps) {
  const blockKeys = (['timer', 'reviews', 'bump', 'security'] as const).filter(
    (key) => skin === 'legacy' || !legacyOnlyBlocks.has(key),
  )

  return (
    <div className={controlStack} data-testid="checkout-config-controls">
      <section className={group}>
        <div className={groupHead}>
          <span className={groupTitle}>Aparência</span>
          <p className={groupHint}>Modelo, cor de destaque e banner desta variante.</p>
        </div>

        <div className={fieldBlock}>
          <span className={fieldLabel}>Modelo</span>
          <SegmentedControl<CheckoutTemplate>
            value={checkout.template}
            options={[
              { label: 'Clássico', value: 'classic' },
              { label: 'Minimalista', value: 'minimal' },
            ]}
            onChange={(value) => onChange({ template: value })}
          />
        </div>

        {experienceMode === 'FORCE_LEGACY' && (
          <Banner tone="info" title="Layout público fixado na versão anterior">
            A administração da plataforma fixou o checkout público na versão anterior. O que o
            comprador vê agora é o estilo do Checkout 2. O Checkout 1 fica salvo para quando o
            layout for liberado.
          </Banner>
        )}
        {experienceMode === 'RANDOM' && (
          <Banner tone="info" title="Layout público em teste">
            Durante um teste da plataforma, parte dos compradores vê a versão anterior do checkout.
            Cada variante guarda o próprio estilo.
          </Banner>
        )}

        <div className={fieldBlock}>
          <span className={fieldLabel}>Cor de destaque</span>
          <div className={inlineRow}>
            {accentOptions.map((accent) => (
              <button
                key={accent}
                type="button"
                className={swatch}
                data-selected={checkout.accent === accent}
                title={checkoutAccentLabels[accent]}
                aria-label={checkoutAccentLabels[accent]}
                aria-pressed={checkout.accent === accent}
                style={{ background: checkoutAccentHex[accent] }}
                onClick={() => onChange({ accent })}
              />
            ))}
            <span className={swatchCaption}>{checkoutAccentLabels[checkout.accent]}</span>
          </div>
        </div>

        <CheckoutBannerField key={skin} checkout={checkout} onChange={onChange} />
      </section>

      <section className={group}>
        <div className={groupHead}>
          <span className={groupTitle}>Campos do formulário</span>
          <p className={groupHint}>Escolha o que o comprador preenche antes de pagar.</p>
        </div>
        <div className={toggleGrid}>
          {(['name', 'email', 'phone', 'document'] as const).map((key) => {
            const required = requiredFields.has(key)
            return (
              <div
                key={key}
                className={toggleCard}
                title={required ? 'Obrigatório para entrega e confirmação' : undefined}
              >
                <span className={nodeTitle}>{fieldLabels[key]}</span>
                <Toggle
                  checked={required || checkout.fields[key]}
                  disabled={required}
                  onChange={(checked) =>
                    onChange({ fields: { ...checkout.fields, [key]: checked, email: true } })
                  }
                  ariaLabel={required ? `${fieldLabels[key]} obrigatório` : fieldLabels[key]}
                />
              </div>
            )
          })}
        </div>
      </section>

      <section className={group}>
        <div className={groupHead}>
          <span className={groupTitle}>Blocos de conversão</span>
          <p className={groupHint}>Elementos de confiança e urgência exibidos no checkout.</p>
        </div>
        {skin === 'new' ? (
          <p className={groupHint}>
            As faixas de oferta por tempo limitado e de compra protegida aparecem só no Checkout 2.
          </p>
        ) : null}
        <div className={toggleGrid}>
          {blockKeys.map((key) => (
            <div key={key} className={toggleCard} title={blockHints[key]}>
              <span className={nodeTitle}>{blockLabels[key]}</span>
              <Toggle
                checked={checkout.blocks[key]}
                onChange={(checked) => onChange({ blocks: { ...checkout.blocks, [key]: checked } })}
                ariaLabel={blockLabels[key]}
              />
            </div>
          ))}
        </div>
      </section>

      <div className={globalMarketCard}>
        <span className={globalMarketIcon} aria-hidden="true">
          <Globe2 size={18} />
        </span>
        <div>
          <span className={nodeTitle}>Mercado global ativo</span>
          <p className={copy}>
            O checkout usa a cotação real para converter automaticamente o preço base para a moeda
            do país do comprador. Exemplo: se seu produto custa R$ 100,00 e o dólar está em R$ 5,00,
            nos Estados Unidos o comprador vê US$ 20,00. Se a moeda local não estiver disponível no
            provedor de pagamento, usamos USD como fallback.
            <br />
            <strong>
              O valor liquidado ao vendedor pode variar ligeiramente por câmbio e taxas do provedor.
            </strong>
          </p>
        </div>
        <span className={globalMarketPill}>Sempre ativo</span>
      </div>
    </div>
  )
}
